<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative">
    <!-- Animated Background -->
    <div class="fixed inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-900/20 via-transparent to-transparent pointer-events-none"></div>
    <div class="fixed inset-0 bg-grid-pattern opacity-10 pointer-events-none"></div>
    
    <!-- Floating Particles -->
    <div class="fixed inset-0 pointer-events-none">
      <div class="particle particle-1"></div>
      <div class="particle particle-2"></div>
      <div class="particle particle-3"></div>
      <div class="particle particle-4"></div>
      <div class="particle particle-5"></div>
    </div>

    <!-- Modern Sidebar Navigation -->
    <div :class="[
      'sidebar fixed inset-y-0 left-0 z-50 bg-slate-800/90 backdrop-blur-xl border-r border-slate-700/50 flex flex-col transition-all duration-300',
      sidebarCollapsed ? 'w-16 is-collapsed' : 'w-72'
    ]">
      <!-- Logo Section -->
      <div :class="[
        'border-b border-slate-700/50 flex-shrink-0 relative',
        sidebarCollapsed ? 'px-2 py-4' : 'p-6'
      ]">
        <a :href="landingPageUrl" :class="[
          'flex items-center group transition-all duration-300',
          sidebarCollapsed ? 'justify-center' : 'space-x-3'
        ]">
          <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy Logo" :class="[
            'rounded-2xl shadow-lg shadow-emerald-500/25 group-hover:scale-105 transition-transform',
            sidebarCollapsed ? 'w-10 h-10' : 'w-12 h-12'
          ]" />
          <span v-show="!sidebarCollapsed" class="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent group-hover:from-emerald-300 group-hover:to-cyan-300 transition-all duration-300">
            OilBuddy
          </span>
          <span v-show="!sidebarCollapsed" class="px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-xs text-emerald-400 font-medium group-hover:bg-emerald-500/20 transition-all duration-300">
            AI
          </span>
        </a>
        
        <!-- Collapse Toggle Button -->
        <button 
          @click="toggleSidebar"
          class="absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-6 h-6 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-full flex items-center justify-center shadow-lg transition-colors duration-200 group"
          :title="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        >
          <svg :class="[
            'w-3 h-3 text-slate-300 transition-transform duration-300',
            sidebarCollapsed ? 'rotate-180' : ''
          ]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      <!-- Navigation Menu -->
      <nav :class="[
        'flex-1 py-6 space-y-2 overflow-y-auto overflow-x-hidden custom-scrollbar',
        sidebarCollapsed ? 'px-2' : 'px-4'
      ]">
        <div class="mb-8">
          <div v-show="!sidebarCollapsed" class="flex items-center px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
            <span>Main</span>
          </div>
          <div class="space-y-1">
            <router-link to="/company" class="nav-item group">
              <img src="/src/assets/images/icons/growth chart.png" alt="Dashboard" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Dashboard</span>
            </router-link>
          </div>
        </div>

        <div class="mb-8">
          <div v-show="!sidebarCollapsed" class="flex items-center px-3 py-2 text-xs font-semibold text-emerald-400/80 uppercase tracking-wider mb-3">
            <span>On Site Tools</span>
          </div>
          <div class="space-y-1">
            <router-link to="/company/post-job" class="nav-item group">
              <img src="/src/assets/images/icons/megaphone announcement.png" alt="Post Job" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Post Job</span>
            </router-link>

            <router-link to="/company/jobs" class="nav-item group">
              <img src="/src/assets/images/icons/checklist and pen.png" alt="Manage Jobs" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Manage Jobs</span>
            </router-link>

            <router-link to="/company/applications" class="nav-item group">
              <img src="/src/assets/images/icons/ai-agent.png" alt="Applications" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Applications</span>
            </router-link>

            <router-link to="/company/crew" class="nav-item group">
              <img src="/src/assets/images/icons/handshake.png" alt="Manage Crew" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Manage Crew</span>
            </router-link>

            <router-link to="/company/scheduling" class="nav-item group">
              <img src="/src/assets/images/icons/red calendar.png" alt="Scheduling" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Scheduling</span>
            </router-link>

            <router-link to="/company/dispatch" class="nav-item group">
              <img src="/src/assets/images/icons/map location.png" alt="Dispatch" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Dispatch</span>
            </router-link>

            <router-link to="/company/timesheets" class="nav-item group">
              <img src="/src/assets/images/icons/calendar with star.png" alt="Timesheets" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Timesheets</span>
            </router-link>

            <router-link to="/company/tank-sheets" class="nav-item group">
              <img src="/src/assets/images/icons/todo-list.png" alt="Tank Sheets" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Tank Sheets</span>
            </router-link>

            <router-link to="/company/well-head-sheets" class="nav-item group">
              <img src="/src/assets/images/icons/checklist and pen.png" alt="Well Head Sheets" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Well Head Sheets</span>
            </router-link>

            <router-link to="/company/field-tickets" class="nav-item group">
              <img src="/src/assets/images/icons/todo-list.png" alt="Field Tickets" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Field Tickets</span>
            </router-link>

            <router-link to="/company/onsite" class="nav-item group">
              <img src="/src/assets/images/icons/map location.png" alt="On Site Hub" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">On Site Hub</span>
            </router-link>

            <router-link to="/company/safety" class="nav-item group">
              <img src="/src/assets/images/icons/shiny blue shield.png" alt="Safety & Compliance" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Safety & Compliance</span>
            </router-link>
          </div>
        </div>

        <div class="mb-8">
          <div v-show="!sidebarCollapsed" class="flex items-center px-3 py-2 text-xs font-semibold text-cyan-400/80 uppercase tracking-wider mb-3">
            <span>CRM</span>
          </div>
          <div class="space-y-1">
            <router-link to="/company/crm" class="nav-item group">
              <img src="/src/assets/images/icons/social media personal.png" alt="CRM Hub" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">CRM Hub</span>
            </router-link>

            <router-link :to="{ path: '/company/crm', query: { tab: 'customers' } }" class="nav-item group">
              <img src="/src/assets/images/icons/customer service.png" alt="Customers" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Customers</span>
            </router-link>

            <router-link to="/company/payments" class="nav-item group">
              <img src="/src/assets/images/icons/hand holding dollar coin.png" alt="Payments" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Payments & Invoices</span>
            </router-link>

            <router-link to="/company/w2-dashboard" class="nav-item group">
              <img src="/src/assets/images/icons/worker badge.png" alt="W-2 Dashboard" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">W-2 Dashboard</span>
            </router-link>

            <router-link to="/company/payroll" class="nav-item group">
              <img src="/src/assets/images/icons/worker badge.png" alt="Payroll" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Payroll (W-2)</span>
            </router-link>

            <router-link to="/company/contractor-payroll" class="nav-item group">
              <img src="/src/assets/images/icons/hand holding dollar coin.png" alt="Contractor Payroll" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Payroll (1099)</span>
            </router-link>

            <router-link to="/company/expenses" class="nav-item group">
              <img src="/src/assets/images/icons/money and dollar.png" alt="Expenses" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Expenses</span>
            </router-link>

            <router-link to="/company/bank" class="nav-item group">
              <img src="/src/assets/images/icons/bank office icon.png" alt="Bank Accounts" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Bank Accounts</span>
            </router-link>

            <router-link to="/company/tax-calculation" class="nav-item group">
              <img src="/src/assets/images/icons/gold dollar coin.png" alt="Tax Calculator" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Tax Calculator</span>
            </router-link>

            <router-link to="/company/ai-tax-optimization" class="nav-item group">
              <img src="/src/assets/images/icons/ai-powered financial research tools.png" alt="AI Tax Optimization" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">AI Tax Optimization</span>
            </router-link>

            <router-link to="/company/profile" class="nav-item group">
              <img src="/src/assets/images/icons/checklist and pen.png" alt="Company Profile" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Company Profile</span>
            </router-link>
          </div>
        </div>

        <div class="mb-8">
          <div v-show="!sidebarCollapsed" class="flex items-center px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
            <span>Platform</span>
          </div>
          <div class="space-y-1">
            <router-link to="/company/ai-messaging" class="nav-item group">
              <img src="/src/assets/images/icons/ai chatbot window.png" alt="AI Assistant" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">AI Assistant</span>
            </router-link>

            <router-link to="/company/knowledge" class="nav-item group">
              <img src="/src/assets/images/icons/books.png" alt="Knowledge Hub" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Knowledge Hub</span>
            </router-link>

            <router-link to="/company/blog" class="nav-item group">
              <img src="/src/assets/images/icons/books.png" alt="Industry News" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Industry News</span>
            </router-link>

            <router-link to="/company/store" class="nav-item group">
              <img src="/src/assets/images/icons/retail shop store.png" alt="Store" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Store</span>
            </router-link>

            <router-link to="/company/marketplace" class="nav-item group">
              <img src="/src/assets/images/icons/ai-marketplace.png" alt="AI Marketplace" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">AI Marketplace</span>
            </router-link>

            <router-link to="/company/integrations" class="nav-item group">
              <img src="/src/assets/images/icons/integrations.png" alt="Integrations" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Integrations</span>
            </router-link>
          </div>
        </div>

        <div class="mb-8">
          <div v-show="!sidebarCollapsed" class="flex items-center px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
            <span>Account</span>
          </div>
          <div class="space-y-1">
            <router-link to="/company/settings" class="nav-item group">
              <img src="/src/assets/images/icons/gear icon with neon.png" alt="Settings" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Settings</span>
            </router-link>

            <router-link to="/company/support" class="nav-item group">
              <img src="/src/assets/images/icons/customer service.png" alt="Support" class="w-6 h-6 mr-3 group-hover:scale-110 transition-transform">
              <span v-show="!sidebarCollapsed">Support</span>
            </router-link>
          </div>
        </div>
      </nav>

      <!-- Company Profile Section -->
      <div :class="[
        'border-t border-slate-700/50 flex-shrink-0',
        sidebarCollapsed ? 'p-2' : 'p-4'
      ]">
        <div
          :class="[
            'flex items-center bg-slate-700/30 rounded-xl hover:bg-slate-600/50 transition-all duration-200 cursor-pointer group mb-3',
            sidebarCollapsed ? 'justify-center p-1.5' : 'space-x-3 p-3'
          ]"
          :title="sidebarCollapsed ? (companyName || 'Company') : ''"
          @click="handleProfileClick"
        >
          <div class="w-10 h-10 flex-shrink-0 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
            <span class="text-sm font-bold text-white">
              {{ companyInitials || 'C' }}
            </span>
          </div>
          <div v-show="!sidebarCollapsed" class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-white group-hover:text-emerald-400 transition-colors truncate">{{ companyName || 'Company' }}</p>
            <p class="text-xs text-slate-400">{{ companyIndustry || 'Industry' }}</p>
          </div>
          <svg v-show="!sidebarCollapsed" class="w-4 h-4 text-slate-400 group-hover:text-emerald-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4"/>
          </svg>
        </div>
        
        <!-- Logout Button -->
        <button
          @click="logout"
          :class="[
            'w-full flex items-center justify-center py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all duration-200',
            sidebarCollapsed ? 'px-0' : 'space-x-2 px-3'
          ]"
          :title="sidebarCollapsed ? 'Sign Out' : ''"
        >
          <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
          </svg>
          <span v-show="!sidebarCollapsed" class="text-sm font-medium">Sign Out</span>
        </button>
      </div>
    </div>

    <!-- Main Content Area -->
    <div :class="[
      'app-content min-h-screen transition-all duration-300',
      sidebarCollapsed ? 'ml-16' : 'ml-72'
    ]">
      <!-- Top Navigation Bar -->
      <div :class="[
        'app-topbar fixed top-0 right-0 z-40 bg-slate-800/90 backdrop-blur-xl border-b border-slate-700/50 transition-all duration-300',
        sidebarCollapsed ? 'left-16' : 'left-72'
      ]">
        <div class="flex items-center justify-between px-6 py-4">
          <!-- Left side - Page title -->
          <div class="flex items-center space-x-4">
            <h1 class="text-xl font-semibold text-white">{{ pageTitle }}</h1>
            <span class="text-sm text-slate-400">{{ pageSubtitle }}</span>
          </div>
          
          <!-- Right side - Navigation items -->
          <div class="flex items-center space-x-4">
            <!-- Notifications -->
            <button class="relative p-2 text-slate-400 hover:text-emerald-400 transition-colors rounded-lg">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-5 5v-5zM15 17H9a2 2 0 01-2-2V5a2 2 0 012-2h6a2 2 0 012 2v10z"/>
              </svg>
              <div class="absolute top-1 right-1 w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
            </button>

            <!-- Settings -->
            <button class="p-2 text-slate-400 hover:text-emerald-400 transition-colors rounded-lg">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </button>

            <!-- Avatar Dropdown -->
            <div class="relative">
              <button @click="showUserDropdown = !showUserDropdown" class="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-800/50 transition-colors">
                <div class="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center">
                  <span class="text-sm font-bold text-slate-900">{{ companyInitials || 'C' }}</span>
                </div>
                <div class="hidden md:block text-left">
                  <p class="text-sm font-medium text-white">{{ companyName || 'Company' }}</p>
                  <p class="text-xs text-slate-400">{{ companyIndustry || 'Industry' }}</p>
                </div>
                <svg class="w-4 h-4 text-slate-400" :class="{ 'rotate-180': showUserDropdown }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>

              <!-- Dropdown Menu -->
              <div v-if="showUserDropdown" class="absolute right-0 mt-2 w-64 bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl shadow-black/20 py-2">
                <!-- Company Info Header -->
                <div class="px-4 py-3 border-b border-slate-700/50">
                  <div class="flex items-center space-x-3">
                    <div class="w-12 h-12 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center">
                      <span class="text-lg font-bold text-slate-900">{{ companyInitials || 'C' }}</span>
                    </div>
                    <div>
                      <p class="text-sm font-semibold text-white">{{ companyName || 'Company' }}</p>
                      <p class="text-xs text-slate-400">{{ companyIndustry || 'Industry' }}</p>
                      <p class="text-xs text-emerald-400 font-medium">Company Account</p>
                    </div>
                  </div>
                </div>

                <!-- Menu Items -->
                <div class="py-2">
                  <router-link 
                    to="/company/profile" 
                    class="flex items-center space-x-3 px-4 py-2 text-slate-300 hover:bg-slate-700/50 hover:text-emerald-400 transition-colors"
                    @click="showUserDropdown = false"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                    <span>Company Profile</span>
                  </router-link>
                  
                  <router-link 
                    to="/company/settings" 
                    class="flex items-center space-x-3 px-4 py-2 text-slate-300 hover:bg-slate-700/50 hover:text-emerald-400 transition-colors"
                    @click="showUserDropdown = false"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    <span>Account Settings</span>
                  </router-link>
                  
                  <router-link 
                    to="/company/support" 
                    class="flex items-center space-x-3 px-4 py-2 text-slate-300 hover:bg-slate-700/50 hover:text-emerald-400 transition-colors"
                    @click="showUserDropdown = false"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"/>
                    </svg>
                    <span>Support</span>
                  </router-link>
                </div>

                <!-- Sign Out Button -->
                <div class="px-4 py-2 border-t border-slate-700/50">
                  <button @click="logout" class="w-full flex items-center space-x-3 px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all duration-200">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
            </svg>
                    <span class="text-sm font-medium">Sign Out</span>
          </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Page Content -->
      <div class="pt-20 p-4">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { getLandingPageUrl } from '../../config/environment';
import { useUserStore } from '../../stores/userStore';

interface Props {
  pageTitle?: string
  pageSubtitle?: string
}

withDefaults(defineProps<Props>(), {
  pageTitle: 'Company Dashboard',
  pageSubtitle: ''
})

const userStore = useUserStore()
const router = useRouter()

const showUserDropdown = ref(false)
const sidebarCollapsed = ref(false)
const landingPageUrl = getLandingPageUrl()

// Computed properties
const companyInitials = computed(() => {
  const name = userStore.currentUser?.name || 'Company'
  return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2)
})

const companyName = computed(() => userStore.currentUser?.name || 'Company')
const companyIndustry = computed(() => userStore.currentUser?.company || 'Industry')

// Methods
const handleProfileClick = () => {
  showUserDropdown.value = !showUserDropdown.value
}

// Sidebar toggle function
const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value
  // Save preference to localStorage
  localStorage.setItem('sidebarCollapsed', sidebarCollapsed.value.toString())
}

const logout = async () => {
  // Clear user data first
  userStore.logout()
  
  // Use router.replace to prevent back button access
  try {
    await router.replace('/login')
    console.log('Logout navigation successful')
    
    // Clear browser history to prevent back button access
    window.history.replaceState(null, '', window.location.href)
  } catch (error) {
    console.error('Logout navigation failed:', error)
    // Fallback to direct navigation
    window.location.replace('/#/login')
  }
}

// Click outside listener
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement
  if (!target.closest('.relative')) {
    showUserDropdown.value = false
  }
}

onMounted(() => {
  // Restore sidebar state from localStorage
  const savedSidebarState = localStorage.getItem('sidebarCollapsed')
  if (savedSidebarState !== null) {
    sidebarCollapsed.value = savedSidebarState === 'true'
  }
  
  userStore.loadUserFromStorage()
  document.addEventListener('click', handleClickOutside)

  // Labels are hidden on the collapsed rail, so fall back to hover tooltips.
  nextTick(() => {
    document.querySelectorAll<HTMLElement>('.sidebar .nav-item').forEach((item) => {
      const label = item.querySelector('img')?.getAttribute('alt')
      if (label) item.setAttribute('title', label)
    })
  })
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Background patterns */
.bg-grid-pattern {
  background-image: radial-gradient(circle, rgba(148, 163, 184, 0.3) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: grid-move 20s linear infinite;
}

@keyframes grid-move {
  0% { background-position: 0 0; }
  100% { background-position: 50px 50px; }
}

/* Floating particles */
.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: linear-gradient(45deg, #10b981, #06b6d4);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.particle-1 { top: 20%; left: 10%; animation-delay: 0s; }
.particle-2 { top: 60%; left: 80%; animation-delay: 2s; }
.particle-3 { top: 80%; left: 20%; animation-delay: 4s; }
.particle-4 { top: 40%; left: 70%; animation-delay: 1s; }
.particle-5 { top: 70%; left: 40%; animation-delay: 3s; }

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 1; }
  50% { transform: translateY(-20px) rotate(180deg); opacity: 0.8; }
}

/* Navigation active states */
.nav-item.router-link-active {
  @apply bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 text-emerald-400 border-l-4 border-emerald-500;
}

.nav-item.router-link-active img {
  @apply scale-110;
}

/* Custom scrollbar */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 116, 139, 0.5) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.5);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.8);
}

/* Navigation styles */
.nav-item {
  @apply flex items-center space-x-3 px-4 py-3 text-slate-300 rounded-xl hover:bg-slate-800/50 hover:text-emerald-400 transition-all duration-300;
}

/* Collapsed rail is 64px wide, so the item padding and icon gutter have to
   collapse too or the icons get pushed outside the sidebar and clipped. */
.sidebar.is-collapsed .nav-item {
  @apply justify-center px-0 space-x-0;
}

.sidebar.is-collapsed .nav-item img {
  @apply mr-0;
}

.sidebar.is-collapsed .nav-item.router-link-active {
  @apply border-l-0 border-b-2 border-emerald-500 rounded-lg;
}

/* Field sheets are printed on paper, so drop the app chrome and the
   sidebar offset that would otherwise waste the left margin. */
@media print {
  .sidebar,
  .app-topbar {
    display: none !important;
  }

  .app-content {
    margin-left: 0 !important;
  }

  .app-content > div:last-child {
    padding-top: 0 !important;
  }
}
</style>
